/**
 * Report templates — one-click bundles of saved reports.
 *
 * The operator asked for "more, templates, create new": instead of hand
 * building each card, a template describes a small dashboard (a named
 * list of metric cards) and `buildReportsFromTemplate` turns it into the
 * exact `SavedReportInput[]` the existing savedReports store persists.
 * `applyReportTemplate` then saves every card — and because saved-report
 * ids are deterministic per name (`rpt:${slug(name)}`), applying a
 * template twice is a clean idempotent upsert, never a duplicate pile.
 *
 * PURITY: the gallery + builder are pure data/functions over the metric
 * registry in reportMetrics.ts — every card's metric is validated against
 * `reportMetricDef` at build time so a template can never reference a
 * metric this build cannot compute. Only `applyReportTemplate` touches
 * persistence (via src/lib/db/savedReports.ts).
 */

import {
  reportMetricDef,
  type ReportCategory,
  type ReportDisplay,
  type ReportMetricKind,
} from './reportMetrics';
import {
  saveReport,
  type SavedReportInput,
} from '../db/savedReports';
import type { SavedReport } from '../db/types';

// --- Shapes -------------------------------------------------------------------

/** One card a template will create — maps 1:1 onto a SavedReport. */
export interface ReportTemplateCard {
  /** The saved report's name. Must be unique within the template. */
  name: string;
  /** Which registry metric the card computes. */
  metric: ReportMetricKind;
  /** Stat card or bar breakdown. */
  display: ReportDisplay;
}

/** A one-click template: a titled bundle of metric cards. */
export interface ReportTemplate {
  id: string;
  title: string;
  description: string;
  /** Drives the accent color/icon in the gallery (reuses CATEGORY_META). */
  category: ReportCategory;
  cards: ReportTemplateCard[];
}

// --- Gallery ------------------------------------------------------------------

/**
 * The stock template gallery. Card names are phrased so their derived
 * ids stay distinct across templates (e.g. the exec rollup prefixes
 * "Exec ·") — applying two templates never silently overwrites cards
 * from another one unless they are genuinely the same report.
 */
export const REPORT_TEMPLATES: readonly ReportTemplate[] = [
  {
    id: 'tpl-sales',
    title: 'Sales',
    description: 'Deals won, the win rate, average deal size, and the book of business.',
    category: 'sales',
    cards: [
      { name: 'Won deals', metric: 'won-deals', display: 'bars' },
      { name: 'Win rate', metric: 'win-rate', display: 'stat' },
      { name: 'Avg deal size', metric: 'avg-deal-size', display: 'stat' },
      { name: 'Book of business', metric: 'book-value', display: 'bars' },
    ],
  },
  {
    id: 'tpl-pipeline',
    title: 'Pipeline',
    description: 'Everything still in play — open value by stage and prospect flow.',
    category: 'sales',
    cards: [
      { name: 'Pipeline value by stage', metric: 'pipeline-value', display: 'bars' },
      { name: 'Open prospects', metric: 'open-prospects', display: 'bars' },
      { name: 'Pipeline avg deal size', metric: 'avg-deal-size', display: 'stat' },
    ],
  },
  {
    id: 'tpl-customer-health',
    title: 'Customer Health',
    description: 'Live accounts, the health average, and both ends of the risk spectrum.',
    category: 'health',
    cards: [
      { name: 'Active customers', metric: 'active-customers', display: 'bars' },
      { name: 'Avg customer health', metric: 'avg-customer-health', display: 'bars' },
      { name: 'At-risk customers', metric: 'at-risk-customers', display: 'stat' },
      { name: 'Healthy customers', metric: 'healthy-customers', display: 'stat' },
    ],
  },
  {
    id: 'tpl-content',
    title: 'Content',
    description: 'Published vs in flight, what is scheduled, and how the work scores.',
    category: 'content',
    cards: [
      { name: 'Content published', metric: 'content-published', display: 'stat' },
      { name: 'Content in flight', metric: 'content-in-flight', display: 'bars' },
      { name: 'Scheduled content', metric: 'content-scheduled', display: 'stat' },
      { name: 'Avg content score', metric: 'avg-content-score', display: 'stat' },
    ],
  },
  {
    id: 'tpl-agent-autonomy',
    title: 'Agent / Autonomy',
    description: 'The honest agent ledger — runs, approvals, hours saved, dollars touched.',
    category: 'agents',
    cards: [
      { name: 'Agent runs', metric: 'agent-runs', display: 'bars' },
      { name: 'Approved agent actions', metric: 'approved-agent-actions', display: 'bars' },
      { name: 'Pending approvals', metric: 'pending-approvals', display: 'stat' },
      { name: 'Operator hours saved', metric: 'agent-hours-saved', display: 'stat' },
      { name: 'Dollars influenced', metric: 'agent-dollars-influenced', display: 'stat' },
    ],
  },
  {
    id: 'tpl-weekly-exec',
    title: 'Weekly Exec',
    description:
      'The Monday-morning rollup: pipeline, wins, risk, content shipped, and agent value.',
    category: 'sales',
    cards: [
      { name: 'Exec · Pipeline value', metric: 'pipeline-value', display: 'stat' },
      { name: 'Exec · Win rate', metric: 'win-rate', display: 'stat' },
      { name: 'Exec · Book of business', metric: 'book-value', display: 'stat' },
      { name: 'Exec · At-risk customers', metric: 'at-risk-customers', display: 'stat' },
      { name: 'Exec · Content published', metric: 'content-published', display: 'stat' },
      { name: 'Exec · Hours saved by agents', metric: 'agent-hours-saved', display: 'stat' },
      { name: 'Exec · Tickets shipped', metric: 'shipped-tickets', display: 'stat' },
    ],
  },
] as const;

/** Look up a template; throws on an unknown id (never apply a lie). */
export function reportTemplateById(id: string): ReportTemplate {
  const tpl = REPORT_TEMPLATES.find((t) => t.id === id);
  if (!tpl) throw new Error(`Unknown report template: ${id}`);
  return tpl;
}

// --- Template → report builder --------------------------------------------------

/**
 * Turn a template into the saved-report inputs it creates. Pure; validates
 * every card so a bad template fails loudly at build time, not render time:
 *   - every metric must exist in REPORT_METRICS (reportMetricDef throws),
 *   - card names must be non-blank and unique within the template
 *     (saved-report ids are name-derived — dupes would silently collapse).
 */
export function buildReportsFromTemplate(
  template: ReportTemplate,
  opts: { createdBy?: string } = {},
): SavedReportInput[] {
  if (template.cards.length === 0) {
    throw new Error(`Report template "${template.id}" has no cards.`);
  }
  const seen = new Set<string>();
  return template.cards.map((card) => {
    const name = card.name.trim();
    if (!name) {
      throw new Error(`Report template "${template.id}" has a card with a blank name.`);
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      throw new Error(
        `Report template "${template.id}" has duplicate card name "${name}".`,
      );
    }
    seen.add(key);
    reportMetricDef(card.metric); // throws on a metric this build can't compute
    return {
      name,
      metric: card.metric,
      display: card.display,
      ...(opts.createdBy !== undefined ? { createdBy: opts.createdBy } : {}),
    };
  });
}

/**
 * Apply a template: persist every card as a saved report. Idempotent —
 * ids are deterministic per name, so re-applying upserts in place.
 * Returns the persisted records in card order.
 */
export async function applyReportTemplate(
  template: ReportTemplate,
  opts: { createdBy?: string } = {},
): Promise<SavedReport[]> {
  const inputs = buildReportsFromTemplate(template, opts);
  const saved: SavedReport[] = [];
  for (const input of inputs) {
    saved.push(await saveReport(input));
  }
  return saved;
}
